/**
 * Operator-facing Operational Issues identity.
 * Uses local canonical rows and stored import snapshots only — no provider calls.
 */

import { extractMarketplaceItemIdentity } from "@/lib/marketplace-identity";
import { exceptionTypeLabel } from "@/lib/operational-issues";
import { channelDisplayLine, optionValuesLabel, sanitizeTechnicalRecord, type ConnectorIdentity } from "@/lib/sync-events-ux";

export interface CanonicalProductHit {
  id: string;
  name: string | null;
}

export interface CanonicalVariantHit {
  id: string;
  product_id: string | null;
  sku: string | null;
  option_values: Record<string, string> | null;
}

export interface OperationalIssueCatalog {
  connectors: Record<string, ConnectorIdentity>;
  productsById: Record<string, CanonicalProductHit>;
  variantsBySku: Record<string, CanonicalVariantHit>;
}

export function emptyOperationalIssueCatalog(): OperationalIssueCatalog {
  return { connectors: {}, productsById: {}, variantsBySku: {} };
}

export function unmappedItemFallback(channelLine: string): string {
  const name = channelLine.replace(/\s*·.*$/, "").trim() || "channel";
  return `Unmapped ${name} item`;
}

export interface PresentedOperationalIssue {
  headline: string;
  secondary: string[];
  channelLine: string;
  messageTitle: string;
  messageDetail: string | null;
  incomingSku: string | null;
  searchText: string;
  technical: { label: string; value: string }[];
}

function orderLabels(externalData: Record<string, unknown> | null | undefined): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const push = (v: unknown) => {
    if (v == null) return;
    const s = String(v).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    ids.push(s.startsWith("#") ? s : `#${s}`);
  };
  if (!externalData) return ids;
  if (Array.isArray(externalData.external_order_ids)) externalData.external_order_ids.forEach(push);
  push(externalData.external_order_id);
  return ids;
}

export function presentOperationalIssue(opts: {
  id: string;
  source: string;
  type: string;
  message: string;
  entityType: string;
  entityId: string | null;
  provider: string | null;
  fingerprint?: string | null;
  entityRef?: string | null;
  connectorId?: string | null;
  externalData?: Record<string, unknown> | null;
  occurrenceCount?: number | null;
  catalog: OperationalIssueCatalog;
}): PresentedOperationalIssue {
  const connector = opts.connectorId ? opts.catalog.connectors[opts.connectorId] : undefined;
  const channelLine = connector
    ? channelDisplayLine(connector)
    : opts.provider
      ? channelDisplayLine({ id: "", name: opts.provider, provider: opts.provider })
      : "Unknown channel";

  const incomingSku = (opts.entityRef ?? "").trim() || null;
  const variant = incomingSku ? opts.catalog.variantsBySku[incomingSku] : undefined;
  const mappedProduct = variant?.product_id ? opts.catalog.productsById[variant.product_id] : undefined;
  const item = extractMarketplaceItemIdentity({ entityRef: opts.entityRef, externalData: opts.externalData });
  const orders = orderLabels(opts.externalData);
  const qty = item.quantity;

  let headline: string;
  const secondary: string[] = [];
  let messageTitle = exceptionTypeLabel(opts.type);
  let messageDetail: string | null = null;

  if (opts.type === "unmapped_product") {
    if (mappedProduct?.name) {
      headline = mappedProduct.name;
      const options = optionValuesLabel(variant?.option_values);
      if (options) secondary.push(options);
      if (incomingSku) secondary.push(`SKU ${incomingSku}`);
      messageTitle = "Not mapped to a Tradivo product";
    } else {
      headline = item.productName || unmappedItemFallback(channelLine);
      if (incomingSku) secondary.push(`Incoming SKU: ${incomingSku}`);
      messageTitle = "Not mapped to a Tradivo product";
    }
    const bits: string[] = [];
    if (orders.length) bits.push(`Order ${orders.join(", ")}`);
    if (qty != null) bits.push(`Qty ${qty}`);
    messageDetail = bits.length ? bits.join(" · ") : null;
  } else if (opts.type === "refund_provider_sync_failed" || opts.type === "refund_provider_rejected") {
    headline = mappedProduct?.name || item.productName || "Refund";
    if (orders.length) secondary.push(`Order ${orders.join(", ")}`);
    const refund = String(opts.externalData?.provider_refund_id ?? "").trim();
    if (refund) secondary.push(`Refund #${refund}`);
    messageTitle = "Refund sync failed";
    messageDetail = opts.message && !opts.message.startsWith("SKU ") ? opts.message : null;
  } else {
    headline = mappedProduct?.name || item.productName || opts.message || exceptionTypeLabel(opts.type);
    if (incomingSku) secondary.push(incomingSku.startsWith("SKU ") ? incomingSku : `SKU ${incomingSku}`);
    messageTitle = exceptionTypeLabel(opts.type);
    messageDetail = opts.message || null;
  }

  const technical = [
    { label: "Issue ID", value: opts.id },
    { label: "fingerprint", value: opts.fingerprint ?? "—" },
    { label: "entity_type", value: opts.entityType || "—" },
    { label: "entity_id", value: opts.entityId ?? "—" },
    { label: "incoming SKU", value: incomingSku ?? "—" },
    { label: "connector_id", value: opts.connectorId ?? "—" },
    { label: "orders", value: orders.join(", ") || "—" },
    { label: "observations", value: String(opts.occurrenceCount ?? 1) },
  ];
  const payload = sanitizeTechnicalRecord(opts.externalData ?? null);
  if (payload) technical.push({ label: "context", value: payload });

  const searchText = [
    headline,
    ...secondary,
    channelLine,
    messageTitle,
    messageDetail,
    incomingSku,
    item.productName,
    opts.type,
    connector?.name,
    ...orders,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    headline,
    secondary,
    channelLine,
    messageTitle,
    messageDetail,
    incomingSku,
    searchText,
    technical,
  };
}
